import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Search as SearchIcon } from 'lucide-react';
import { useState } from 'react';

export function WikiSearch() {
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');

  const searchQuery = useQuery({
    queryKey: ['wiki', 'search', submitted],
    queryFn: () => api.wikiSearch(submitted, { limit: 30 }),
    enabled: submitted.length > 0,
  });

  return (
    <div className="mx-auto max-w-6xl px-6 py-10">
      <header className="mb-6">
        <Link
          to="/wikis"
          className="mb-2 inline-block text-xs text-foreground-muted hover:text-accent"
        >
          ← 위키 목차
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">위키 검색</h1>
      </header>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted(q.trim());
        }}
        className="mb-6 flex gap-2"
      >
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground-faint" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="검색어 입력 (한국어/영어)"
            className="w-full rounded-md border border-border bg-surface py-2 pl-10 pr-3 text-sm focus:border-accent focus:outline-none"
          />
        </div>
        <button
          type="submit"
          className="rounded-md border border-border bg-surface px-4 text-sm font-medium hover:border-accent"
        >
          검색
        </button>
      </form>

      {submitted.length === 0 ? (
        <p className="text-sm text-foreground-muted">
          검색어를 입력해 주세요. 제목/takeaway/본문을 모두 검색합니다.
        </p>
      ) : searchQuery.isLoading ? (
        <p className="text-sm text-foreground-muted">검색 중…</p>
      ) : searchQuery.isError ? (
        <p className="text-sm text-destructive">
          검색 실패: {(searchQuery.error as Error).message}
        </p>
      ) : (
        <>
          <p className="mb-3 text-sm text-foreground-muted">
            <strong>"{submitted}"</strong> — {searchQuery.data?.results.length ?? 0}건
          </p>
          <ul className="space-y-2">
            {searchQuery.data?.results.map((r) => {
              // 외부 sync (KMS 등) 출처가 있으면 그 URL 새 탭에서 열기 — innermate 내부 페이지로
              // 굳이 우회하지 않는다. 없으면 기존처럼 /wikis/p/$slug.
              const body = (
                <>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-medium">{r.title}</span>
                    {r.categorySlug ? (
                      <span className="text-xs text-foreground-faint">{r.categorySlug}</span>
                    ) : null}
                  </div>
                  <div className="mt-1 text-sm text-foreground-muted line-clamp-2">
                    {r.takeaway}
                  </div>
                  <div className="mt-1 text-xs text-foreground-faint">{r.snippet}</div>
                </>
              );
              return (
                <li
                  key={r.slug}
                  className="rounded-md border border-border bg-surface p-3 hover:border-accent"
                >
                  {r.externalUrl ? (
                    <a href={r.externalUrl} target="_blank" rel="noreferrer" className="block">
                      {body}
                    </a>
                  ) : (
                    <Link to="/wikis/p/$slug" params={{ slug: r.slug }} className="block">
                      {body}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
